<template>
  <main class="main-content flex-grow-1 p-4 overflow-auto mt-6" style="background-color: #f8f9fa;">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="text-header-page">Integrations</h2>
    </div>

    <!-- Sandbox Mode Alert -->
    <div v-if="environment === 'sandbox'" class="alert alert-warning mb-4 text-white" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      You are currently on sandbox mode, where all operations are testing purpose only and do not affect live data
    </div>

    <div class="row justify-content-center">
      <div class="col-12 px-0">
        
        <!-- Upgrade Account Message -->
        <div v-if="statusKyb !== 'approved'" class="card card-custom m-4 upgrade-message">
          <div class="card-body text-center py-5">
            <div class="mb-4">
              <i class="fas fa-lock fa-3x text-muted"></i>
            </div>
            <h4 class="text-muted mb-3">Account Upgrade Required</h4>
            <p class="text-muted mb-4">
              To access the Integrations menu and view your credentials, you need to upgrade your account first.
              <br>
              Please complete your account verification process to continue.
            </p>
            <router-link to="/home" class="btn btn-primary">
              <i class="fas fa-user-check me-2"></i>
              Complete Account Verification
            </router-link>
          </div>
        </div>

        <!-- Credentials Section - Only show if approved -->
        <div v-if="statusKyb === 'approved'" class="card card-custom m-4">
          <div class="card-body">
            <h5 class="credentials-title mb-4">Credentials</h5>
            
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label credential-label">App ID</label>
                <div class="credential-field">
                  <input 
                    type="text" 
                    class="form-control credential-input" 
                    :value="credentials.appId" 
                    readonly
                  >
                  <button class="btn btn-link copy-btn" @click="copyToClipboard(credentials.appId)">
                    <i class="far fa-copy"></i>
                  </button>
                </div>
              </div>
              
              <div class="col-md-6 mb-3">
                <label class="form-label credential-label">App Secret</label>
                <div class="credential-field">
                  <input 
                    :type="showAppSecret ? 'text' : 'password'" 
                    class="form-control credential-input" 
                    :value="credentials.appSecret" 
                    readonly
                  >
                  <button class="btn btn-link toggle-btn me-2" @click="showAppSecret = !showAppSecret">
                    <i :class="showAppSecret ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                  </button>
                  <button class="btn btn-link copy-btn" @click="copyToClipboard(credentials.appSecret)">
                    <i class="far fa-copy"></i>
                  </button>
                </div>
              </div>
              
              <!-- <div class="col-md-6 mb-3">
                <label class="form-label credential-label">App Key</label>
                <div class="credential-field">
                  <input 
                    :type="showAppKey ? 'text' : 'password'" 
                    class="form-control credential-input" 
                    :value="credentials.appKey" 
                    readonly
                  >
                  <button class="btn btn-link toggle-btn me-2" @click="showAppKey = !showAppKey">
                    <i :class="showAppKey ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                  </button>
                  <button class="btn btn-link copy-btn" @click="copyToClipboard(credentials.appKey)">
                    <i class="far fa-copy"></i>
                  </button>
                </div>
              </div> -->
              
              <!-- <div class="col-md-6 mb-3">
                <label class="form-label credential-label">Public Key (For Merchant)</label>
                <div class="credential-field">
                  <input 
                    type="text" 
                    class="form-control credential-input" 
                    value="-----BEGIN PUBLIC ****"
                    readonly
                  >
                  <button class="btn btn-link copy-btn" @click="copyToClipboard(credentials.publicKey)">
                    <i class="far fa-copy"></i>
                  </button>
                </div>
              </div> -->
            </div>
          </div>
        </div>

        <!-- Callback URL Section - Only show if approved -->
        <div v-if="statusKyb === 'approved'" class="card card-custom m-4">
          <div class="card-body">
            <h5 class="section-title mb-4">Callback URL</h5>
            
            <div class="row">
              <div class="col-12 mb-3">
                <label class="form-label credential-label">Callback URL</label>
                <div class="d-flex gap-2">
                  <input 
                    type="url" 
                    class="form-control" 
                    v-model="callbackUrl"
                    placeholder="https://example.com/callback"
                    :disabled="isUpdatingCallback"
                  >
                  <button 
                    class="btn btn-primary save-btn" 
                    @click="updateCallbackUrl"
                    :disabled="isUpdatingCallback || !callbackUrl"
                  >
                    <i v-if="isUpdatingCallback" class="fas fa-spinner fa-spin me-2"></i>
                    <i v-else class="fas fa-save me-2"></i>
                    {{ isUpdatingCallback ? 'Saving...' : 'Save' }}
                  </button>
                </div>
                <small class="text-muted">
                  Enter your callback URL where webhook notifications will be sent
                </small>
                
                <!-- Example Request Format -->
                <div class="mt-3 p-3 bg-light rounded">
                  <h6 class="text-dark mb-2">
                    <i class="fas fa-info-circle me-2"></i>
                    Example Request Format
                  </h6>
                  <p class="text-muted mb-2 small">
                    Your callback URL will receive POST requests with the following JSON structure:
                  </p>
                  <pre class="bg-dark text-light p-3 rounded mb-0" style="font-size: 12px; overflow-x: auto;"><code>{
  "transaction_id": "100028355123792503",
  "amount": "10000",
  "status": "Success",
  "reference_id": "REFID153966210",
  "admin_fee": "3500"
}</code></pre>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Public Key Section -->
        <!-- <div class="card card-custom m-4">
          <div class="card-body">
            <h5 class="section-title mb-3">Public Key</h5>
            
            <div class="mb-3">
              <label for="publicKeyTextarea" class="form-label">Public Key*</label>
              <textarea 
                id="publicKeyTextarea"
                class="form-control public-key-textarea" 
                rows="8"
                v-model="merchantPublicKey"
                placeholder="-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA04DHJn2rPJ5Mm3xRW26P
UCipdpQBsJUJVSP8qEbXjP1jvwPfyGksS5UJCY4AVPKZS5bx7Q/2F2mxv/Vi7V3PL
6f3nK4tFwvGYF+xE6Qy71tJGjxfj0Iaq1vcGGZ5X5B7X5ZjDw6UW3Z5F5Y1W3Q1
TESTJDhKKvjFhZoZjKOyKOwBwLfoxOQThsUjJQl0VXOGjK2hqBQPxvSJoVXzGzOqp
7zQ1/6vOSgQgEV6BQI2lPQ3J2KGKqE7YV8vQx9eU+DjpKlKzOp6s7+ynG8/u/WU+cQ
KQJKBLbQzF8rKzG7W3v1Q9nGvY1FzQv7OvGkOcJ4t2sKqJ5wRsQ3Y7Kz7t1YvVQ0p
3wIDAQAB
-----END PUBLIC KEY-----"
              ></textarea>
            </div>
            
            <div class="public-key-info">
              <p class="info-text"><strong>Material you need to provide us:</strong></p>
              <p class="info-text">
                You need to generate a pair of public and private keys use OpenSSL's genpkey command to generate a pair of 2048-bit public and private keys, and then provide the public key to us
              </p>
              <p class="info-text">
                <strong>Private key:</strong> openssl genpkey -algorithm RSA -out private_key.pem -pkeyopt rsa_keygen_bits:2048
              </p>
              <p class="info-text">
                <strong>Public key:</strong> openssl rsa -pubout -in private_key.pem -out public_key.pem
              </p>
              <p class="info-text">
                We will generate the corresponding app key based on your public key and provide it to you
              </p>
            </div>
            
            <button class="btn btn-primary save-btn">Save</button>
          </div>
        </div> -->

        <!-- IP Allowlist Section -->
        <!-- <div class="card card-custom m-4">
          <div class="card-body">
            <h5 class="section-title mb-3">IP Allowlist</h5>
            
            <p class="info-text mb-3">
              For your convenience, please provide the Public Key to the IT support team and we will complete the configuration for you.
            </p>
            
            <p class="info-text mb-4">
              Secure API access against foreign or malicious IPs by allowing only specific IP addresses of your choice to access APIs via ip Allowlist. The IP Allowlist only works for API users through direct integration and will not work for Plugin users (Shopify, Woocommerce, etc)
            </p>
            
            <div class="ip-allowlist-container">
              <div v-for="(ip, index) in ipAllowlist" :key="index" class="ip-row mb-2">
                <div class="input-group">
                  <input 
                    type="text" 
                    class="form-control ip-input" 
                    v-model="ipAllowlist[index]"
                    placeholder="Enter IP Address/CIDR"
                  >
                  <button 
                    class="btn btn-outline-danger delete-ip-btn" 
                    type="button"
                    @click="removeIpAddress(index)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              
              <button 
                class="btn btn-success btn-sm mt-2" 
                @click="addIpAddress"
              >
                <i class="fas fa-plus me-2"></i>Add IP Address/CIDR
              </button>
            </div>
          </div>
        </div> -->
        
      </div>
    </div>
  </main>
</template>

<script>
import { updateCallbackUrl } from '../../api/transaction';
import { getProfile } from '../../api/company';
import Swal from 'sweetalert2';

export default {
  name: "IntegrationsView",
  data() {
    return {
      credentials: {
        appId: '',
        appSecret: '',
        appKey: '',
        publicKey: ''
      },
      merchantPublicKey: '',
      ipAllowlist: [''],
      showAppSecret: false,
      showAppKey: false,
      callbackUrl: '',
      isUpdatingCallback: false
    };
  },
  computed: {
    environment() {
      return this.$store.getters.currentEnvironment;
    },
    statusKyb() {
      return this.$store.getters.statusKyb;
    }
  },
  watch: {
    environment: {
      handler() {
        this.getCredentials(); // Generate new credentials when environment changes
      }
    }
  },
  methods: {
    getCredentials() {
      console.log("Getting credentials from localStorage...");
      const appId = localStorage.getItem("merchantAppId");
      const appSecret = localStorage.getItem("merchantAppSecret");
      
      this.credentials = {
        appId: appId,
        appSecret: appSecret,
        // appKey: `8e00a22f-7${generateRandomString(12)}`,
        publicKey: this.generatePublicKey()
      };
      
    },
    
    generatePublicKey() {
      return null
    },
    
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        // You can add a toast notification here
        console.log('Copied to clipboard');
      }).catch(err => {
        console.error('Failed to copy: ', err);
      });
    },
    
    addIpAddress() {
      this.ipAllowlist.push('');
    },
    
    removeIpAddress(index) {
      if (this.ipAllowlist.length > 1) {
        this.ipAllowlist.splice(index, 1);
      }
    },
    
    handleEnvironmentChange() {
      this.getCredentials();
      this.loadCallbackUrl();
    },
    
    async loadCallbackUrl() {
      try {
        const response = await getProfile();
        if (response.status === "success" && response.data.merchant.callback_url) {
          this.callbackUrl = response.data.merchant.callback_url;
        }
      } catch (error) {
        console.error('Error loading callback URL:', error);
      }
    },
    
    async updateCallbackUrl() {
      if (!this.callbackUrl) {
        Swal.fire('Error', 'Please enter a callback URL', 'error');
        return;
      }
      
      this.isUpdatingCallback = true;
      
      try {
        const result = await updateCallbackUrl(this.callbackUrl);
        Swal.fire('Success', 'Callback URL updated successfully!', 'success');
        console.log('Callback URL updated:', result);
        
        // Reload callback URL to ensure UI is synced with server
        await this.loadCallbackUrl();
      } catch (error) {
        console.error('Error updating callback URL:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
        Swal.fire('Error', `Failed to update callback URL: ${errorMessage}`, 'error');
      } finally {
        this.isUpdatingCallback = false;
      }
    }
  },
  
  mounted() {
    this.getCredentials();
    this.loadCallbackUrl();
    
    // Debug: Log statusKyb value
    console.log("Status KYB:", this.statusKyb);
    console.log("LocalStorage merchantKyb:", localStorage.getItem("merchantKyb"));
    
    // Listen for environment change events
    window.addEventListener('environmentChanged', this.handleEnvironmentChange);
  },
  
  beforeUnmount() {
    // Clean up event listener
    window.removeEventListener('environmentChanged', this.handleEnvironmentChange);
  }
};
</script>

<style scoped>
.text-header-page {
  font-size: 28px;
  font-weight: 600;
  color: #121313;
  margin: 0;
}

.card-custom {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.credentials-title,
.section-title {
  color: #121313;
  font-size: 20px;
  font-weight: 600;
  border: 2px solid #dc3545;
  border-radius: 8px;
  padding: 8px 16px;
  display: inline-block;
  margin-bottom: 20px;
}

.credential-label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 5px;
}

.credential-field {
  position: relative;
  display: flex;
  align-items: center;
}

.credential-input {
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 8px 70px 8px 12px;
  background-color: #f8f9fa;
}

.copy-btn {
  position: absolute;
  right: 8px;
  border: none;
  background: none;
  color: #6c757d;
  padding: 4px;
}

.copy-btn:hover {
  color: #495057;
}

.toggle-btn {
  position: absolute;
  right: 36px;
  border: none;
  background: none;
  color: #6c757d;
  padding: 4px;
}

.toggle-btn:hover {
  color: #495057;
}

.public-key-textarea {
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.4;
  resize: vertical;
}

.public-key-info {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
}

.info-text {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 8px;
  line-height: 1.5;
}

.save-btn {
  background-color: #007bff;
  border-color: #007bff;
  border-radius: 6px;
  padding: 8px 24px;
  font-weight: 500;
}

.ip-allowlist-container {
  max-width: 500px;
}

.ip-row {
  display: flex;
  align-items: center;
}

.ip-input {
  border-radius: 6px 0 0 6px;
  height: 38px;
}

.delete-ip-btn {
  border-radius: 0 6px 6px 0;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 45px;
  padding: 0;
  margin: 0;
  line-height: 1;
}

.input-group {
  display: flex;
  width: 100%;
  align-items: stretch;
}

.input-group .form-control {
  flex: 1;
  margin: 0;
}

.input-group .btn {
  border-left: 0;
  margin: 0;
}

.alert-warning {
  border-left: 4px solid #ffc107;
  background-color: #fff8e1;
  border-radius: 6px;
}

.upgrade-message {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
}

.upgrade-message .fas {
  color: #6c757d;
}

@media (max-width: 768px) {
  .text-header-page {
    font-size: 24px;
  }
}

.d-flex.gap-2 {
  gap: 0.5rem;
}

.d-flex.gap-2 .form-control {
  flex: 1;
}

.d-flex.gap-2 .save-btn {
  flex-shrink: 0;
  min-width: 100px;
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.fa-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 